import { Html, Line, OrbitControls } from '@react-three/drei';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Maximize2, Minus, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import type { CameraTarget, GraphNode, GraphRelation } from '../data/types';
import { useColorTheme } from '../lib/theme';
import type { HoverTarget } from './NodeHoverCard';

interface GraphSceneProps {
  nodes: GraphNode[];
  relations: GraphRelation[];
  selectedId?: string;
  activeNodeIds?: string[];
  activeRelationIds?: string[];
  mode: 'research' | 'presentation';
  profile?: 'exhibition' | 'presenter';
  cameraFocus?: 'all' | 'selected' | 'narrative';
  cameraTarget?: CameraTarget;
  labelMode?: 'key' | 'all';
  onSelect: (nodeId: string) => void;
  onClearSelection?: () => void;
  onHover?: (target?: HoverTarget) => void;
}

const nodeColors: Record<GraphNode['type'], string> = {
  company: '#c1583f',
  person: '#d2ae6d',
  architecture: '#8ea39b',
  place: '#6c8e91',
  machine: '#d9c9a5',
  technology: '#b77861',
  process: '#aabda7',
  textile: '#e1d8c5',
  document: '#c0a76f',
  photograph: '#e4e0d5',
  map: '#7ba8a0',
  event: '#d66f4e',
  trade: '#b99b63',
  heritage: '#b9cbc1',
};

function pointFor(node: GraphNode, selectedId: string | undefined, activeNodeIds: Set<string>): THREE.Vector3 {
  const selected = node.id === selectedId;
  const active = activeNodeIds.has(node.id);
  const pressure = selected ? 0.23 : active ? 0.1 : 0;
  const length = Math.hypot(node.layout.x, node.layout.y) || 1;
  return new THREE.Vector3(
    node.layout.x + (node.layout.x / length) * pressure,
    node.layout.y + (node.layout.y / length) * pressure,
    node.layout.z + (selected ? 0.32 : active ? 0.14 : 0),
  );
}

function strainFor(relation: GraphRelation, selectedId: string | undefined, activeRelationIds: Set<string>): number {
  if (activeRelationIds.has(relation.id)) return 1;
  if (selectedId && (relation.from === selectedId || relation.to === selectedId)) return 0.8;
  return 0;
}

function Edge({ relation, from, to, selectedId, activeRelationIds, mode, light }: { relation: GraphRelation; from: GraphNode; to: GraphNode; selectedId?: string; activeRelationIds: Set<string>; mode: 'research' | 'presentation'; light: boolean }): JSX.Element {
  const strain = strainFor(relation, selectedId, activeRelationIds);
  const fromPoint = pointFor(from, selectedId, new Set());
  const toPoint = pointFor(to, selectedId, new Set());
  const midpoint = new THREE.Vector3().addVectors(fromPoint, toPoint).multiplyScalar(0.5);
  const normal = new THREE.Vector3(-(toPoint.y - fromPoint.y), toPoint.x - fromPoint.x, 0).normalize();
  const bend = normal.multiplyScalar((relation.id.charCodeAt(2) % 2 === 0 ? 1 : -1) * (0.06 + strain * 0.14));
  midpoint.add(bend);
  const points = useMemo(() => [fromPoint, midpoint, toPoint], [fromPoint.x, fromPoint.y, fromPoint.z, midpoint.x, midpoint.y, midpoint.z, toPoint.x, toPoint.y, toPoint.z]);
  const color = strain > 0 ? (light ? '#a84531' : '#d66f4e') : (light ? '#82958d' : '#3d504b');
  const opacity = strain > 0 ? 0.9 : mode === 'presentation' ? (light ? 0.24 : 0.14) : (light ? 0.48 : 0.4);
  return <Line points={points} color={color} transparent opacity={opacity} lineWidth={strain > 0 ? 1.4 : mode === 'presentation' ? 0.42 : 0.65} />;
}

function NodePoint({ node, position, selected, active, onSelect, onHover, showLabel }: { node: GraphNode; position: THREE.Vector3; selected: boolean; active: boolean; onSelect: () => void; onHover?: (target?: HoverTarget) => void; showLabel: boolean }): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const color = nodeColors[node.type];
  const size = selected ? 0.14 : node.featured ? 0.105 : 0.075;
  const reportHover = (event: { nativeEvent: Event }): void => {
    const pointer = event.nativeEvent as PointerEvent;
    onHover?.({ nodeId: node.id, x: pointer.clientX, y: pointer.clientY });
  };
  return (
    <group position={position}>
      <mesh onClick={(event) => { event.stopPropagation(); onSelect(); }} onPointerOver={(event) => { event.stopPropagation(); setHovered(true); reportHover(event); document.body.style.cursor = 'crosshair'; }} onPointerMove={reportHover} onPointerOut={() => { setHovered(false); onHover?.(undefined); document.body.style.cursor = ''; }}>
        <sphereGeometry args={[size, selected ? 20 : 12, selected ? 20 : 12]} />
        <meshBasicMaterial color={color} transparent opacity={active || selected ? 1 : 0.72} />
      </mesh>
      {(selected || active) && <mesh scale={selected ? 1.8 : 1.45}>
        <ringGeometry args={[size * 1.35, size * 1.55, 32]} />
        <meshBasicMaterial color={selected ? '#e0a04d' : '#9ab8ae'} transparent opacity={selected ? 0.9 : 0.55} side={THREE.DoubleSide} />
      </mesh>}
      {(showLabel || (hovered && !onHover)) && <Html position={[node.layout.x >= 0 ? size * 0.9 : -size * 0.9, size + 0.1, 0]} center={node.layout.x < 0} distanceFactor={10} style={{ pointerEvents: 'none' }}>
        <span className={`node-label ${selected ? 'is-selected' : ''} ${hovered ? 'is-hovered' : ''}`}>{node.title}</span>
      </Html>}
    </group>
  );
}

function CameraRig({ nodes, focusNode, cameraFocus, cameraTarget, profile, zoomLevel, fitRequest }: { nodes: GraphNode[]; focusNode?: GraphNode; cameraFocus: 'all' | 'selected' | 'narrative'; cameraTarget?: CameraTarget; profile: 'exhibition' | 'presenter'; zoomLevel: number; fitRequest: number }): JSX.Element {
  const { camera } = useThree();
  const controls = useRef<any>(null);
  const frame = useMemo(() => {
    if (cameraFocus === 'selected' && focusNode) {
      const target = new THREE.Vector3(focusNode.layout.x, focusNode.layout.y, focusNode.layout.z);
      const distance = profile === 'exhibition' ? 9.8 : 8.2;
      return { target, position: new THREE.Vector3(target.x * 0.22, target.y * 0.22, distance) };
    }
    if (cameraFocus === 'narrative' && cameraTarget) {
      const targetNode = cameraTarget.nodeId ? nodes.find((node) => node.id === cameraTarget.nodeId) : undefined;
      const target = cameraTarget.position
        ? new THREE.Vector3(...cameraTarget.position)
        : targetNode
          ? new THREE.Vector3(targetNode.layout.x, targetNode.layout.y, targetNode.layout.z)
          : new THREE.Vector3(0, 0, 0);
      const lookAt = cameraTarget.lookAt ? new THREE.Vector3(...cameraTarget.lookAt) : target;
      const distance = cameraTarget.distance ?? (profile === 'exhibition' ? 9.2 : 7.8);
      return { target: lookAt, position: new THREE.Vector3(target.x * 0.22, target.y * 0.22, target.z + distance) };
    }
    const box = new THREE.Box3();
    nodes.forEach((node) => box.expandByPoint(new THREE.Vector3(node.layout.x, node.layout.y, node.layout.z)));
    // Zostawiamy margines na etykiety HTML, żeby węzły przy krawędzi nie były ucinane.
    if (!box.isEmpty()) box.expandByScalar(0.72);
    const target = box.isEmpty() ? new THREE.Vector3(0, 0, 0) : box.getCenter(new THREE.Vector3());
    const size = box.isEmpty() ? new THREE.Vector3(8, 6, 2) : box.getSize(new THREE.Vector3());
    const perspective = camera as THREE.PerspectiveCamera;
    const aspect = Math.max(perspective.aspect, 0.55);
    const visibleHeight = Math.max(size.y, size.x / aspect);
    const fov = THREE.MathUtils.degToRad(perspective.fov || 42);
    const distance = THREE.MathUtils.clamp((visibleHeight / (2 * Math.tan(fov / 2))) * 1.25, 10, 30);
    return { target, position: new THREE.Vector3(target.x, target.y, distance) };
  }, [camera, cameraFocus, cameraTarget, fitRequest, focusNode, nodes, profile]);
  const desired = useRef(frame.position.clone());
  const target = useRef(frame.target.clone());
  const animating = useRef(true);
  const previousZoom = useRef(zoomLevel);

  useEffect(() => {
    desired.current.copy(frame.position);
    target.current.copy(frame.target);
    animating.current = true;
  }, [frame]);

  useEffect(() => {
    const delta = zoomLevel - previousZoom.current;
    previousZoom.current = zoomLevel;
    if (!delta || !controls.current) return;
    const controlTarget = controls.current.target as THREE.Vector3;
    const direction = camera.position.clone().sub(controlTarget).normalize();
    const currentDistance = camera.position.distanceTo(controlTarget);
    const nextDistance = THREE.MathUtils.clamp(currentDistance * (delta > 0 ? 0.78 : 1.28), 3.4, 36);
    camera.position.copy(controlTarget).addScaledVector(direction, nextDistance);
    animating.current = false;
    controls.current.update();
  }, [camera, zoomLevel]);

  useFrame(() => {
    if (animating.current) {
      camera.position.lerp(desired.current, 0.07);
      if (camera.position.distanceTo(desired.current) < 0.03) animating.current = false;
    }
    if (controls.current) {
      if (animating.current) controls.current.target.lerp(target.current, 0.07);
      controls.current.update();
    }
  });

  return <OrbitControls ref={controls} enableDamping dampingFactor={0.08} enablePan={false} zoomToCursor minDistance={3.4} maxDistance={36} rotateSpeed={0.36} zoomSpeed={0.7} />;
}

function SceneContent({ nodes, relations, selectedId, activeNodeIds = [], activeRelationIds = [], mode, profile = 'exhibition', cameraFocus = 'all', cameraTarget, labelMode = 'key', zoomLevel, fitRequest, onSelect, onHover, light }: GraphSceneProps & { zoomLevel: number; fitRequest: number; light: boolean }): JSX.Element {
  const activeSet = useMemo(() => new Set(activeNodeIds), [activeNodeIds]);
  const activeRelationSet = useMemo(() => new Set(activeRelationIds), [activeRelationIds]);
  const nodeMap = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const focusNode = selectedId ? nodeMap.get(selectedId) : nodes.find((node) => node.featured) ?? nodes[0];
  const showLabel = labelMode === 'all';
  return (
    <>
      <color attach="background" args={[light ? '#e3ded3' : '#101513']} />
      <ambientLight intensity={0.8} />
      <group>
        {relations.map((item) => {
          const from = nodeMap.get(item.from);
          const to = nodeMap.get(item.to);
          if (!from || !to) return null;
          return <Edge key={item.id} relation={item} from={from} to={to} selectedId={selectedId} activeRelationIds={activeRelationSet} mode={mode} light={light} />;
        })}
        {nodes.map((node) => <NodePoint key={node.id} node={node} position={pointFor(node, selectedId, activeSet)} selected={node.id === selectedId} active={activeSet.has(node.id)} onSelect={() => onSelect(node.id)} onHover={onHover} showLabel={showLabel || node.id === selectedId || (mode === 'presentation' && activeSet.has(node.id))} />)}
      </group>
      <CameraRig nodes={nodes} focusNode={focusNode} cameraFocus={cameraFocus} cameraTarget={cameraTarget} profile={profile} zoomLevel={zoomLevel} fitRequest={fitRequest} />
    </>
  );
}

export function GraphScene(props: GraphSceneProps): JSX.Element {
  const theme = useColorTheme();
  const [zoomLevel, setZoomLevel] = useState(0);
  const [fitRequest, setFitRequest] = useState(0);
  return (
    <div className="graph-canvas" aria-label="Interaktywny graf Bielska-Białej">
      <Canvas camera={{ position: [0, 0, 18], fov: 42 }} dpr={[1, 2]} gl={{ antialias: true }} onPointerMissed={() => props.onClearSelection?.()}>
        <SceneContent {...props} zoomLevel={zoomLevel} fitRequest={fitRequest} light={theme === 'light'} />
      </Canvas>
      <div className="graph-controls" aria-label="Sterowanie widokiem grafu">
        <button onClick={() => setZoomLevel((value) => value + 1)} aria-label="Przybliż graf" title="Przybliż"><Plus size={15} /></button>
        <button onClick={() => setZoomLevel((value) => value - 1)} aria-label="Oddal graf" title="Oddal"><Minus size={15} /></button>
        <button onClick={() => setFitRequest((value) => value + 1)} aria-label="Pokaż cały graf" title="Pokaż cały graf"><Maximize2 size={14} /></button>
      </div>
    </div>
  );
}
